import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { LucideIcon } from './LucideIcon';
import { useSimpleAuth } from '../lib/supabase';
import { supabase } from '../lib/supabase';

export function UserProfile() {
  const [isOpen, setIsOpen] = useState(false);
  const [profileData, setProfileData] = useState<any>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { user, isAdmin, loading, signOut } = useSimpleAuth();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch profile data when user is available
  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) {
        setProfileData(null);
        return;
      }

      try {
        // Get latest image from bucket first
        const { data: files } = await supabase.storage
          .from('profile-images')
          .list(user.id, {
            limit: 10,
            sortBy: { column: 'created_at', order: 'desc' }
          });

        let latestAvatarUrl = '';
        if (files && files.length > 0) {
          const validExtensions = ['jpg', 'jpeg', 'png', 'webp', 'gif'];
          const latestImage = files.find(file => {
            const ext = file.name.split('.').pop()?.toLowerCase();
            return ext && validExtensions.includes(ext);
          });
          
          if (latestImage) {
            const { data: { publicUrl } } = supabase.storage
              .from('profile-images')
              .getPublicUrl(`${user.id}/${latestImage.name}`);
            latestAvatarUrl = publicUrl;
          }
        }

        // Get profile from database
        const { data } = await supabase
          .from('admin_profiles')
          .select('*')
          .eq('id', user.id)
          .single();

        const profileInfo = {
          id: user.id,
          display_name: data?.display_name || user.email?.split('@')[0] || 'User',
          avatar_url: latestAvatarUrl || data?.avatar_url || '',
          email: user.email,
          role: data?.role || 'admin'
        };

        setProfileData(profileInfo);
      } catch (error) {
        // Fallback profile
        setProfileData({
          id: user.id,
          display_name: user.email?.split('@')[0] || 'User',
          avatar_url: '',
          email: user.email,
          role: 'admin'
        });
      }
    };

    fetchProfile();

    // Listen for profile updates
    const handleProfileUpdate = () => fetchProfile();
    window.addEventListener('profile-updated', handleProfileUpdate);

    return () => {
      window.removeEventListener('profile-updated', handleProfileUpdate);
    };
  }, [user]);

  const handleSignOut = async () => {
    try {
      await signOut();
      setIsOpen(false);
      setProfileData(null);
    } catch (error) {
      // Handle error silently
    }
  };

  const dropdownVariants = {
    hidden: { opacity: 0, y: -10, scale: 0.95 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.2 } },
    exit: { opacity: 0, y: -10, scale: 0.95, transition: { duration: 0.15 } }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center w-8 h-8 rounded-full">
        <div className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Not signed in - show profile icon with dropdown
  if (!user) {
    return (
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="relative flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
          aria-label="User menu"
        >
          <LucideIcon name="lucide:user" className="w-4 h-4 text-gray-600 dark:text-gray-400" />
        </button>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              variants={dropdownVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-xl shadow-lg ring-1 ring-black/5 dark:ring-white/10 z-50"
            >
              <div className="p-1">
                <Link
                  to="/login"
                  onClick={() => setIsOpen(false)}
                  className="flex items-center gap-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <LucideIcon name="lucide:log-in" className="w-4 h-4" />
                  Sign In
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // Signed in - show profile
  const displayName = profileData?.display_name || 'User';
  const avatarUrl = profileData?.avatar_url;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative flex items-center gap-2 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
      >
        {/* Profile Image or Fallback */}
        <div className="relative w-8 h-8 rounded-full overflow-hidden">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt="Profile"
              className="w-full h-full object-cover"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const fallback = target.nextElementSibling as HTMLElement;
                if (fallback) fallback.style.display = 'flex';
              }}
            />
          ) : null}
          <div 
            className={`absolute inset-0 bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center ${avatarUrl ? 'hidden' : ''}`}
          >
            <LucideIcon name="lucide:user" className="w-4 h-4 text-white" />
          </div>
        </div>

        {/* Online indicator - small green dot */}
        <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 rounded-full border border-white dark:border-gray-900" />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            variants={dropdownVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-xl shadow-lg ring-1 ring-black/5 dark:ring-white/10 z-50"
          >
            {/* Profile Header */}
            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full overflow-hidden">
                  {avatarUrl ? (
                    <img src={avatarUrl} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                      <LucideIcon name="lucide:user" className="w-5 h-5 text-white" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {displayName}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {profileData?.email}
                  </p>
                </div>
              </div>
            </div>

            {/* Menu Items */}
            <div className="p-1">
              <Link
                to="/admin/dashboard"
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <LucideIcon name="lucide:layout-dashboard" className="w-4 h-4" />
                Dashboard
              </Link>

              <Link
                to="/admin/profile"
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <LucideIcon name="lucide:user" className="w-4 h-4" />
                Profile Settings
              </Link>

              <hr className="my-1 border-gray-100 dark:border-gray-700" />

              <button
                onClick={handleSignOut}
                className="flex w-full items-center gap-3 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
              >
                <LucideIcon name="lucide:log-out" className="w-4 h-4" />
                Sign Out
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}